/**
 * Ultimate Oscillator (UO)
 *
 * Larry Williams' multi-timeframe momentum oscillator combining
 * three different periods with weighted averages.
 *
 * Category: Momentum | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const ultimateOscIndicator: IndicatorDefinition = {
  id: "ultimateosc",
  name: "Ultimate Oscillator",
  shortName: "UO",
  category: "momentum",
  pane: "separate",
  params: [
    { key: "p1", label: "Period 1", type: "number", default: 7, min: 1, max: 100 },
    { key: "p2", label: "Period 2", type: "number", default: 14, min: 2, max: 200 },
    { key: "p3", label: "Period 3", type: "number", default: 28, min: 5, max: 400 },
    { key: "color", label: "Color", type: "color", default: "#673AB7" },
  ],
  plots: [
    { key: "uo", label: "UO", color: "#673AB7", style: "line", lineWidth: 2, priceScaleId: "uo" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { p1, p2, p3 } = params;
    const data: { time: number; value: number }[] = [];
    const maxP = Math.max(p1, p2, p3);

    if (candles.length < maxP + 1) return [{ key: "uo", data }];

    // Buying Pressure & True Range arrays
    const bp: number[] = [0];
    const tr: number[] = [candles[0].high - candles[0].low];

    for (let i = 1; i < candles.length; i++) {
      const trueLow = Math.min(candles[i].low, candles[i - 1].close);
      const trueHigh = Math.max(candles[i].high, candles[i - 1].close);
      bp.push(candles[i].close - trueLow);
      tr.push(trueHigh - trueLow);
    }

    for (let i = maxP; i < candles.length; i++) {
      let bp1 = 0, tr1 = 0, bp2 = 0, tr2 = 0, bp3 = 0, tr3 = 0;
      for (let j = i - p1 + 1; j <= i; j++) { bp1 += bp[j]; tr1 += tr[j]; }
      for (let j = i - p2 + 1; j <= i; j++) { bp2 += bp[j]; tr2 += tr[j]; }
      for (let j = i - p3 + 1; j <= i; j++) { bp3 += bp[j]; tr3 += tr[j]; }

      const avg1 = tr1 === 0 ? 0 : bp1 / tr1;
      const avg2 = tr2 === 0 ? 0 : bp2 / tr2;
      const avg3 = tr3 === 0 ? 0 : bp3 / tr3;

      const uo = 100 * (4 * avg1 + 2 * avg2 + avg3) / 7;
      data.push({ time: candles[i].time, value: uo });
    }

    return [{ key: "uo", data }];
  },
};
